import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { MailPlus, Loader2, Send, Sparkles, BellRing, Compass } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const cfg = { withCredentials: true };

const KINDS = [
  { id: "welcome", label: "Welcome", icon: Sparkles, desc: "Login link, plus the temporary password if they haven't signed in yet" },
  { id: "reminder", label: "Welcome reminder", icon: BellRing, desc: "A friendly nudge to sign in for the first time" },
  { id: "winback", label: "Where have you been?", icon: Compass, desc: "Win-back email with Woodrow's value and gain/loss since their last visit" },
];

const money = (n) => `$${Math.abs(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const activityHint = (ctx) => {
  if (!ctx) return "Loading activity…";
  if (ctx.never_logged_in && !ctx.last_active) return "Never logged in";
  if (ctx.days_inactive == null) return "No activity recorded";
  if (ctx.days_inactive === 0) return "Active today";
  return `Last active ${ctx.days_inactive} day${ctx.days_inactive === 1 ? "" : "s"} ago`;
};

const KindOption = ({ k, active, onClick }) => {
  const Icon = k.icon;
  return (
    <button type="button" onClick={onClick} data-testid={`email-kind-${k.id}`}
      className={`w-full text-left flex items-start gap-3 px-3 py-2.5 border transition-colors ${
        active ? "border-bt-gold bg-bt-gold/10" : "border-bt-border-subtle hover:bg-bt-surface-2"}`}>
      <Icon className={`w-4 h-4 mt-0.5 shrink-0 ${active ? "text-bt-gold" : "text-bt-text-tertiary"}`} />
      <span>
        <span className={`block text-sm font-mono ${active ? "text-bt-gold" : "text-bt-text-primary"}`}>{k.label}</span>
        <span className="block text-[11px] text-bt-text-tertiary">{k.desc}</span>
      </span>
    </button>
  );
};

const ContextStrip = ({ ctx }) => (
  <div className="flex flex-wrap gap-x-4 gap-y-1 text-[11px] font-mono text-bt-text-secondary" data-testid="email-context">
    <span data-testid="email-activity-hint" className={ctx?.days_inactive >= 30 ? "text-bt-negative" : ""}>{activityHint(ctx)}</span>
    {ctx?.woodrow && (
      <span data-testid="email-woodrow-hint">
        Woodrow {money(ctx.woodrow.value)}{" "}
        <span className={ctx.woodrow.change >= 0 ? "text-bt-positive" : "text-bt-negative"}>
          ({ctx.woodrow.change >= 0 ? "+" : "-"}{money(ctx.woodrow.change)} {ctx.woodrow.since_start ? "since start" : "since last visit"})
        </span>
      </span>
    )}
    {ctx?.last_email_sent_at && <span>Last email {new Date(ctx.last_email_sent_at).toLocaleDateString()}</span>}
  </div>
);

export default function EmailUserDialog({ u, onSent }) {
  const [open, setOpen] = useState(false);
  const [kind, setKind] = useState("welcome");
  const [ctx, setCtx] = useState(null);
  const [preview, setPreview] = useState(null);
  const [sending, setSending] = useState(false);
  const base = `${BACKEND}/api/admin/users/${encodeURIComponent(u.email)}`;

  useEffect(() => {
    if (!open) return;
    setCtx(null);
    axios.get(`${base}/email-context`, cfg).then(({ data }) => {
      setCtx(data);
      setKind(data.never_logged_in ? (data.welcome_sent_at ? "reminder" : "welcome") : (data.days_inactive >= 30 ? "winback" : "welcome"));
    }).catch(() => setCtx({}));
  }, [open, base]);

  useEffect(() => {
    if (!open || !ctx) return;
    const ctrl = new AbortController();
    setPreview(null);
    axios.get(`${base}/email-preview?kind=${kind}`, { ...cfg, signal: ctrl.signal })
      .then(({ data }) => setPreview(data))
      .catch((e) => { if (!axios.isCancel(e)) setPreview({ subject: "", html: "" }); });
    return () => ctrl.abort();
  }, [open, kind, base, ctx]);

  const send = async () => {
    setSending(true);
    try {
      const { data } = await axios.post(`${base}/send-email`, { kind }, cfg);
      onSent(kind, data.sent_at);
      toast.success(`"${KINDS.find(k => k.id === kind).label}" sent to ${u.email}`);
      setOpen(false);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to send email"); }
    setSending(false);
  };

  return (
    <>
      <Button variant="ghost" size="sm" onClick={() => setOpen(true)} title="Send email" data-testid={`email-user-${u.email}`}
        className="h-7 w-[30px] px-0 text-bt-gold hover:bg-bt-gold/10">
        <MailPlus className="w-3.5 h-3.5" />
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl bg-bt-surface border-bt-border-subtle text-bt-text-primary" data-testid="email-user-dialog">
          <DialogHeader>
            <DialogTitle className="font-mono">Email {u.name || u.email}</DialogTitle>
            <DialogDescription className="text-bt-text-tertiary">{u.email}</DialogDescription>
          </DialogHeader>
          <ContextStrip ctx={ctx} />
          <div className="grid md:grid-cols-[240px_1fr] gap-4">
            <div className="space-y-2">
              {KINDS.map(k => <KindOption key={k.id} k={k} active={kind === k.id} onClick={() => setKind(k.id)} />)}
            </div>
            <div className="border border-bt-border-subtle bg-black min-h-[320px] flex flex-col">
              <div className="px-3 py-2 border-b border-bt-border-subtle text-xs font-mono text-bt-text-secondary truncate" data-testid="email-preview-subject">
                Subject: {preview?.subject || "…"}
              </div>
              {preview ? (
                <iframe title="Email preview" srcDoc={preview.html} sandbox="" className="flex-1 w-full min-h-[300px] bg-[#0b0b0b]" data-testid="email-preview-frame" />
              ) : (
                <div className="flex-1 flex items-center justify-center"><Loader2 className="w-5 h-5 text-bt-gold animate-spin" /></div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)} data-testid="email-user-cancel">Cancel</Button>
            <Button onClick={send} disabled={sending || !preview} className="bg-bt-gold text-black hover:bg-bt-gold/80" data-testid="email-user-send">
              {sending ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Send className="w-3.5 h-3.5 mr-1" />} Send
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
